import { Component, OnInit } from '@angular/core';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { WindowService } from 'src/shared/providers/window.service';
import { JobRequestWorkflows } from 'src/pages/job-request-workflows/job-request-workflows';
import { Client } from 'src/providers/client/client';
import { Pros } from 'src/providers/pros/pros';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Util } from 'src/shared/util/util';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';
import { ModalController } from '@ionic/angular';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
    templateUrl: 'pro-properties.html',
    standalone: false
})

export class ProPropertiesPage implements OnInit {

  properties: any[] = [];
  filteredProperties: any[] = [];
  propertiesByService: any = {};
  filteredPropertiesByService: any = {};
  serviceCategories: any[] = [];
  filteredServiceCategories: string[] = [];
  proName = '';
  proId: number;
  isRightSideContent = true;
  dialogParams: any;
  loaded = false;
  searchForm: UntypedFormGroup;
  addPropertiesForm: UntypedFormGroup;
  showAddProperties = false;
  missingProperties: any[] = [];
  selectedMissingProperties: any[] = [];
  allAddresses: any[] = [];
  isAddingProperties = false;
  activeServiceCategory: any = null;

  boundAddToProperties = this.addToProperties.bind(this);
  boundCancelAddProperties = this.cancelAddProperties.bind(this);

constructor(
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    public windowService: WindowService,
    private client: Client,
    private pros: Pros,
    private fb: UntypedFormBuilder,
    private util: Util,
    private modalController: ModalController
  ) {
    this.searchForm = this.fb.group({
      search: ['']
    });
    this.addPropertiesForm = this.fb.group({
      addType: [''],
      selectedProperties: [[]]
    });
    this.searchForm.valueChanges
      .pipe(debounceTime(300))
      .subscribe((val) => this.filterProperties(val.search));
  }

  async ngOnInit() {
    try {
      this.rightSidePanelService.setDialogLoading(true);
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      this.dialogParams = await this.storage.retrieve('dialog-params');
      this.proId = this.navCtrl.getParam('proId') || this.dialogParams?.proId;
      this.allAddresses = await this.client.getMoreDetailAddresses();
      await this.loadServiceCategories();
      await this.loadAssignedProperties();
      this.filteredProperties = [...this.properties];
      this.proName = this.navCtrl.getParam('proName') || this.dialogParams?.proName || '';
      this.organizePropertiesByService();
      const title = this.proName ? `${this.proName}'s Properties` : 'Properties';
      this.rightSidePanelService.setDialogPageTitle(title);
      this.rightSidePanelService.setDialogLoading(false);
      this.loaded = true;
      this.rightSidePanelService.setDialogLoading(false);
    } catch (error) {
      const errorMessage = (error.error && error.error.message) ? error.error.message : error.message;
      this.util.showError(errorMessage);
    }
  }

  async loadAssignedProperties(): Promise<void> {
    const allPros = await this.pros.getAllPros();
    const currentPro = allPros.find(p => p.id === this.proId);
    if (currentPro?.address_homekeeper_priorities) {
      this.properties = [];
      currentPro.address_homekeeper_priorities.forEach(priority => {
        const address = this.allAddresses.find(addr => addr.id === priority.address_id);
        if (address) {
          const serviceCategory = this.serviceCategories.find(cat => cat.id === priority.service_type_id);
          this.properties.push({
            id: address.id,
            name: address.address_name || address.address1,
            color: address.color || '#1E2125',
            serviceTypeId: priority.service_type_id,
            serviceName: serviceCategory?.name,
            serviceIcon: serviceCategory?.icon,
            serviceCategoryId: serviceCategory?.id
          });
        }
      });
    }
  }

  async loadServiceCategories() {
    const categories = this.navCtrl.getParam('serviceCategories') || this.dialogParams?.serviceCategories;
    this.serviceCategories = categories.map(cat => ({
      id: cat.id,
      name: cat.name === 'Regular Cleaning' ? 'Cleaning' : cat.name,
      icon: cat.icon_url || 'assets/img/mop.png'
    }));
  }

  organizePropertiesByService(propertiesToOrganize: any[] = null) {
    const props = propertiesToOrganize || this.filteredProperties;
    const organized: any = {};
    props.forEach(property => {
      const serviceName = property.serviceName || 'Unknown';
      if (!organized[serviceName]) {
        organized[serviceName] = {
          name: serviceName,
          icon: property.serviceIcon,
          properties: []
        };
      }
      organized[serviceName].properties.push(property);
    });
    this.serviceCategories.forEach(category => {
      if (!organized[category.name]) {
        organized[category.name] = {
          name: category.name,
          icon: category.icon,
          properties: []
        };
      }
    });
    if (propertiesToOrganize) {
      this.filteredPropertiesByService = organized;
      this.filteredServiceCategories = Object.keys(organized);
    } else {
      this.propertiesByService = organized;
      this.filteredPropertiesByService = { ...organized };
      this.filteredServiceCategories = Object.keys(organized);
    }
  }

  filterProperties(searchTerm: string) {
    if (!searchTerm || searchTerm.trim() === '') {
      this.filteredProperties = [...this.properties];
      this.filteredServiceCategories = [...this.serviceCategories];
    } else {
      const term = searchTerm.toLowerCase();
      this.filteredProperties = this.properties.filter(property => 
        property.name.toLowerCase().includes(term) || 
        property.serviceName.toLowerCase().includes(term)
      );
    }
    this.organizePropertiesByService(this.filteredProperties);
    if (searchTerm && searchTerm.trim() !== '') {
      const term = searchTerm.toLowerCase();
      this.filteredServiceCategories = Object.keys(this.filteredPropertiesByService)
      .filter(key => {
        const hasProperties = this.filteredPropertiesByService[key].properties.length > 0;
        const categoryNameMatches = key.toLowerCase().includes(term);
        return hasProperties || categoryNameMatches;
      });
    }
  }

  identifyMissingPropertiesForService(serviceTypeId: number) {
    this.missingProperties = [];
    const assignedPropertyIds = new Set(
      this.properties
        .filter(p => p.serviceTypeId === serviceTypeId)
        .map(p => p.id)
    );
    this.allAddresses.forEach(address => {
      if (!assignedPropertyIds.has(address.id)) {
        this.missingProperties.push({
          viewValue: address.address_name || address.address1,
          value: address.id,
          id: address.id,
          name: address.address_name || address.address1,
          color: address.color || '#1E2125'
        });
      }
    });
  }

  toggleAddPropertiesForService(serviceCategory: any) {
    if (this.activeServiceCategory?.id === serviceCategory.id) {
      this.filteredPropertiesByService[serviceCategory.name].showAddProperties= false;
      this.activeServiceCategory = null;
      this.selectedMissingProperties = [];
    } else {
      this.activeServiceCategory = serviceCategory;
      this.identifyMissingPropertiesForService(serviceCategory.id);
      this.selectedMissingProperties = this.missingProperties;
      this.addPropertiesForm.patchValue({ addType: 'all' });
      this.filteredPropertiesByService[serviceCategory.name].showAddProperties= true;
    }
    this.addPropertiesForm.patchValue({ selectedProperties: [] });
    if (this.missingProperties.length === 0) {
      this.filteredPropertiesByService[serviceCategory.name].showAddProperties= false;
      this.util.showError(`${this.proName  } is already assigned to all properties for ${  serviceCategory.name.toLowerCase()}`);
    }
  }

  cancelAddProperties() {
    if (this.activeServiceCategory) {
      this.toggleAddPropertiesForService(this.activeServiceCategory);
    }
  }

  onPropertySelectionChange() {
    const selectedIds = this.addPropertiesForm.get('selectedProperties')?.value || [];
    this.selectedMissingProperties = this.missingProperties.filter(prop => 
      selectedIds.includes(prop.value)
    );
  }

  async addToProperties() {
    if (this.selectedMissingProperties.length === 0) {
      this.util.showError('Please select at least one property to add');
      return;
    }
    if (!this.activeServiceCategory) {
      this.util.showError('No service category selected');
      return;
    }
    this.isAddingProperties = true;
    const propertyCount = this.selectedMissingProperties.length;
    try {
      await Promise.all(this.selectedMissingProperties.map(property => {
        const payload = {
          service_type_id: this.activeServiceCategory.id,
          homekeeper_ids: [this.proId]
        };
        return this.pros.addExistingPros(payload, property.id);
      }));
      // Cache invalidation now handled by Pros service via TanStack Query
      await this.loadAssignedProperties();
      this.filteredProperties = [...this.properties];
      this.organizePropertiesByService();
      this.identifyMissingPropertiesForService(this.activeServiceCategory.id);
      this.showAddProperties = false;
      this.selectedMissingProperties = [];
      this.util.showSuccess(`Added ${this.proName} to ${propertyCount} ${propertyCount === 1 ? 'property' : 'properties'} for ${this.activeServiceCategory.name}`);
      this.activeServiceCategory = null;
    } catch (_error) {
      this.util.showError('Failed to add to properties. Please try again.');
    } finally {
      this.isAddingProperties = false;
    }
  }

  async goToProperty(property: any) {
    const addresses = await this.client.getAddresses();
    const fullAddress = addresses.find(addr => addr.id === property.id);
    localStorage.setItem('addressId', property.id.toString());
    localStorage.setItem('selectedCategoryId', property.serviceTypeId.toString());
    const params = {
      addressId: property.id,
      categoryId: property.serviceTypeId,
      address: fullAddress
    };
    this.rightSidePanelService.navigateTo('job-request-workflows', params, JobRequestWorkflows);
  }

  getServiceCategoryById(categoryName: string): any {
    const category = this.serviceCategories.find((cat: any) => cat.name === categoryName);
    if (!category) {
      const filteredCategory = this.filteredPropertiesByService[categoryName];
      if (filteredCategory) {
        const firstProperty = this.properties.find(p => p.serviceName === categoryName);
        return {
          id: firstProperty?.serviceTypeId || 1,
          name: categoryName,
          icon: filteredCategory.icon
        };
      }
    }
    return category;
  }

  async removeProFromCategory(property: any) {
    const isLastProperty = this.properties.length === 1;
    const body = isLastProperty ?
      `${new TranslationPipe().transform('This is the only property ') + this.proName + new TranslationPipe().transform(' is added to. If you remove them, they will no longer appear in your pros list. To use them again, you will need to add them as a new pro with their email or phone.')}` :
      `${new TranslationPipe().transform('Are you sure you want to remove ') + this.proName + new TranslationPipe().transform(' from ') + property.serviceName + new TranslationPipe().transform(' at ') + property.name  }?`;
    const confirmParams: ConfirmPageParamsModel = {
      title: 'Remove Pro?',
      body,
      backText: 'Go Back',
      confirmText: 'Remove Pro',
      confirmAction: this.confirmRemoveProFromCategory.bind(this, property)
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: confirmParams,
      animated: false,
      cssClass: 'confirm-modal'
    });
    confirmationModal.present();
  }

  async confirmRemoveProFromCategory(property: any) {
    const payload = {
      service_type_id: property.serviceCategoryId,
      homekeeper_ids: [this.proId]
    }
    await this.pros.removeProFromAddressList(property.id, payload);
    // Cache invalidation now handled by Pros service via TanStack Query
    await this.loadAssignedProperties();
    this.filteredProperties = [...this.properties];
    this.organizePropertiesByService();
    this.util.showSuccess(`${this.proName  } removed from ${  property.serviceName  } at ${  property.name}`);
    this.modalController.dismiss();
  }

  updateAddType() {
    const addType = this.addPropertiesForm.get('addType')?.value;
    if (addType === 'all') {
      this.selectedMissingProperties = this.missingProperties;
    } else {
      this.selectedMissingProperties = [];
    }
  }

}